import { useState } from 'react'

interface ClaudeResponse {
  content: string
  error?: string
}

interface Attorney {
  name: string
  phone: string
  specialty: string
  reason: string
}

export function useClaudeAPI() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Mock implementation since we can't make actual API calls to Anthropic from here
  // In a real app, this would call a backend proxy or the API directly if safe

  const generateCaseSummary = async (caseData: any): Promise<string> => {
    setIsLoading(true)
    setError(null)

    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 2000))

      const summary = `CASE SUMMARY
      
OVERVIEW
This case involves a series of ${caseData.incidents.length} documented incidents primarily categorized as ${caseData.incidents[0]?.category || 'various issues'}. The documentation spans from ${caseData.incidents[caseData.incidents.length - 1]?.datetime ? new Date(caseData.incidents[caseData.incidents.length - 1].datetime).toLocaleDateString() : 'start'} to present.

TIMELINE ANALYSIS
A clear pattern of escalating behavior is observed. The most recent incident occurred on ${new Date().toLocaleDateString()}.

KEY INCIDENTS
- ${caseData.incidents[0]?.title || 'Initial Incident'}: ${caseData.incidents[0]?.description || 'No description'}
${caseData.incidents.length > 1 ? `- ${caseData.incidents[1]?.title}: ${caseData.incidents[1]?.description}` : ''}

EVIDENCE & WITNESSES
The client has collected ${caseData.evidence.length} items of evidence and identified ${caseData.people.length} key individuals involved.

IMPACT
The client reports significant emotional distress, with ${caseData.checkins.length} health check-ins documenting the ongoing toll.

RECOMMENDATION
Based on the documentation, this case shows strong potential for legal action regarding workplace safety and harassment. Immediate consultation with an employment attorney is recommended.`

      return summary
    } catch (err) {
      setError('Failed to generate summary. Please try again.')
      return ''
    } finally {
      setIsLoading(false)
    }
  }

  const findAttorneys = async (
    location: string,
    caseType: string,
  ): Promise<Attorney[]> => {
    setIsLoading(true)
    setError(null)

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000))

      // Mock results
      return [
        {
          name: 'Sarah Jenkins, Esq.',
          phone: '(555) 123-4567',
          specialty: 'Employment Law & Harassment',
          reason: 'Specializes in workplace harassment cases in ' + location,
        },
        {
          name: 'Miller & Associates',
          phone: '(555) 987-6543',
          specialty: 'Civil Rights',
          reason:
            'Strong track record with discrimination cases similar to yours',
        },
        {
          name: 'Robert Chang',
          phone: '(555) 456-7890',
          specialty: 'Labor Law',
          reason: '20 years experience representing plaintiffs in ' + location,
        },
        {
          name: 'Justice Legal Group',
          phone: '(555) 222-3333',
          specialty: 'Workplace Safety',
          reason:
            'Known for aggressive representation in unsafe condition cases',
        },
        {
          name: 'Elena Rodriguez',
          phone: '(555) 777-8888',
          specialty: 'Personal Injury & Employment',
          reason:
            'Dual specialty relevant to your injury and harassment claims',
        },
      ]
    } catch (err) {
      setError('Failed to find attorneys. Please try again.')
      return []
    } finally {
      setIsLoading(false)
    }
  }

  return { generateCaseSummary, findAttorneys, isLoading, error }
}
